import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  changePasswordRequest,
  forgotPasswordRequest,
  getProfileRequest,
  getStatuteTooltipUrlRequest,
  setPasswordRequest,
  updateLanguageRequest,
} from 'api/requests/profile-requests';
import { CompanyRole } from 'constants/CompanyRole';
import {
  PROFILE__FORGOT_PASSWORD,
  PROFILE__FORGOT_PASSWORD_ERROR,
  PROFILE__FORGOT_PASSWORD_SUCCESS,
  PROFILE__GENERAL_LOADING,
  PROFILE__GET,
  PROFILE__SET,
  PROFILE__SET_HAS_SELECTED_COMPANY,
  PROFILE__SET_PASSWORD,
  PROFILE__SET_PASSWORD_ERROR,
  PROFILE__SET_PASSWORD_SUCCESS,
  PROFILE__STATUTE_TOOLTIP_URL,
  PROFILE__UPDATE_LANGUAGE,
  PROFILE__UPDATE_LANGUAGE_ERROR,
  PROFILE__UPDATE_LANGUAGE_SUCCESS,
  PROFILE__UPDATE_PASSWORD,
} from 'store/constants';
import IForgotPasswordRequest from 'types/profile/IForgotPasswordRequest';
import IProfile from 'types/profile/IProfile';
import IProfileUpdateLanguage from 'types/profile/IProfileUpdateLanguage';
import IChangePassword from 'types/profile/IProfileUpdatePassword';
import ISetPasswordRequest from 'types/profile/ISetPasswordRequest';
import { logoutAction } from './auth-actions';

export const setProfileRequestsLoadingAction = createAction<boolean>(PROFILE__GENERAL_LOADING);

export const setProfileAction = createAction<IProfile>(PROFILE__SET);
export const setHasSelectedCompany = createAction<boolean>(PROFILE__SET_HAS_SELECTED_COMPANY);

export const fetchProfileAsync = createAsyncThunk(PROFILE__GET, async (__, thunkApi) => {
  thunkApi.dispatch(setProfileRequestsLoadingAction(true));
  try {
    const profile = await getProfileRequest();

    if (profile) {
      const persistedCompanyId = localStorage.getItem('selectedCompanyId');
      if (persistedCompanyId) {
        profile.currentCompany = persistedCompanyId;
        thunkApi.dispatch(setHasSelectedCompany(true));
      }

      if (profile.role === CompanyRole.EMPLOYEE && profile.userCompanies.length > 1 && !persistedCompanyId) {
        profile.currentCompany = '';
      }

      thunkApi.dispatch(setProfileAction(profile));
    }
  } catch (err) {
    // If profile has an error
    // Redirect on login page with errors (maybe?)
    thunkApi.dispatch(logoutAction(true));
  }
  thunkApi.dispatch(setProfileRequestsLoadingAction(false));
});

export const changePasswordAction = createAsyncThunk(
  PROFILE__UPDATE_PASSWORD,
  async (data: IChangePassword, thunkApi) => {
    thunkApi.dispatch(setProfileRequestsLoadingAction(true));
    try {
      const success = await changePasswordRequest(data);
      return success;
    } catch (err) {
      console.error('Error changing password:', err);
    } finally {
      thunkApi.dispatch(setProfileRequestsLoadingAction(false));
    }
    return false;
  },
);

export const updateLanguageSuccessAction = createAction<boolean>(PROFILE__UPDATE_LANGUAGE_SUCCESS);
export const updateLanguageErrorAction = createAction<boolean>(PROFILE__UPDATE_LANGUAGE_ERROR);
export const updateLanguageAsync = createAsyncThunk(
  PROFILE__UPDATE_LANGUAGE,
  async (data: IProfileUpdateLanguage, thunkApi) => {
    thunkApi.dispatch(setProfileRequestsLoadingAction(true));
    thunkApi.dispatch(updateLanguageErrorAction(false));
    thunkApi.dispatch(updateLanguageSuccessAction(false));
    try {
      await updateLanguageRequest(data);
      thunkApi.dispatch(updateLanguageSuccessAction(true));
      const profile = await getProfileRequest();
      if (profile) {
        thunkApi.dispatch(setProfileAction(profile));
      }
    } catch (err) {
      thunkApi.dispatch(updateLanguageErrorAction(true));
    }
    thunkApi.dispatch(setProfileRequestsLoadingAction(false));
  },
);

export const setPasswordSuccessAction = createAction<boolean>(PROFILE__SET_PASSWORD_SUCCESS);
export const setPasswordErrorAction = createAction<boolean>(PROFILE__SET_PASSWORD_ERROR);

export const setPasswordAsync = createAsyncThunk(PROFILE__SET_PASSWORD, async (data: ISetPasswordRequest, thunkApi) => {
  thunkApi.dispatch(setProfileRequestsLoadingAction(true));
  thunkApi.dispatch(setPasswordErrorAction(false));
  thunkApi.dispatch(setPasswordSuccessAction(false));
  try {
    await setPasswordRequest(data);
    thunkApi.dispatch(setPasswordSuccessAction(true));
  } catch (err) {
    thunkApi.dispatch(setPasswordErrorAction(true));
  }
  thunkApi.dispatch(setProfileRequestsLoadingAction(false));
});

export const forgotPasswordSuccessAction = createAction<boolean>(PROFILE__FORGOT_PASSWORD_SUCCESS);
export const forgotPasswordErrorAction = createAction<boolean>(PROFILE__FORGOT_PASSWORD_ERROR);

export const forgotPasswordRequestAsync = createAsyncThunk(
  PROFILE__FORGOT_PASSWORD,
  async (data: IForgotPasswordRequest, thunkApi) => {
    thunkApi.dispatch(setProfileRequestsLoadingAction(true));
    thunkApi.dispatch(forgotPasswordSuccessAction(false));
    thunkApi.dispatch(forgotPasswordErrorAction(false));
    try {
      await forgotPasswordRequest(data);
      thunkApi.dispatch(forgotPasswordSuccessAction(true));
    } catch (err) {
      thunkApi.dispatch(forgotPasswordErrorAction(true));
    }

    thunkApi.dispatch(setProfileRequestsLoadingAction(false));
  },
);

export const getStatuteTooltipUrlAction = createAsyncThunk(PROFILE__STATUTE_TOOLTIP_URL, async (__, thunkApi) => {
  thunkApi.dispatch(setProfileRequestsLoadingAction(true));
  try {
    const url = await getStatuteTooltipUrlRequest();
    return url;
  } catch (err) {
    console.error('Error fetching statute tooltip URL:', err);
  } finally {
    thunkApi.dispatch(setProfileRequestsLoadingAction(false));
  }
  return '';
});
